import { useCallback, useEffect, useRef, useState } from "react";
import { admin } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Database, Sparkles, AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";

const fmtInt = (n) => Number(n || 0).toLocaleString("en-IN");

export default function MaintenancePanel() {
  const [dryRunBusy, setDryRunBusy] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [preview, setPreview] = useState(null);          // dry-run response
  const [status, setStatus] = useState(null);            // /status poll response
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await admin.backfillStatus();
      setStatus(data);
      return data;
    } catch (e) {
      console.warn("backfill status fetch failed", e);
      return null;
    }
  }, []);

  // Refresh status on mount + when polling is active
  useEffect(() => {
    fetchStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchStatus]);

  // Start polling whenever we know a run is in progress
  useEffect(() => {
    const running = status?.running;
    if (running && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const s = await fetchStatus();
        if (!s?.running && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          toast.success(`Backfill complete · ${fmtInt(s?.last_run?.total_affected || s?.total_affected)} rows updated`);
        }
      }, 3000);
    } else if (!running && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [status?.running, fetchStatus]);

  const doDryRun = async () => {
    setDryRunBusy(true);
    try {
      const { data } = await admin.backfillAi(true);
      setPreview(data);
      toast.success(`Preview: ${fmtInt(data.total_line_items)} line items pending backfill`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dry-run failed");
    } finally {
      setDryRunBusy(false);
    }
  };

  const doRealRun = async () => {
    setSubmitBusy(true);
    try {
      const { data } = await admin.backfillAi(false);
      if (data?.accepted) {
        toast.success(`Backfill queued · ${fmtInt(data.total_target)} rows in batches of ${fmtInt(data.batch_size)}`);
        // Kick off polling
        await fetchStatus();
      } else {
        toast.error("Unexpected response from server");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Backfill could not start";
      toast.error(msg);
    } finally {
      setSubmitBusy(false);
    }
  };

  const running = !!status?.running;
  const lastRun = status?.last_run;
  const progress = status?.total_target > 0
    ? Math.min(100, Math.round(((status?.total_affected || 0) / status.total_target) * 100))
    : 0;

  return (
    <div className="space-y-4" data-testid="maintenance-panel">
      <div className="bg-cyan-50 border border-cyan-200 rounded-sm p-3 text-[12px] text-slate-700 flex items-start gap-2">
        <Sparkles size={14} className="mt-0.5 text-cyan-700 flex-shrink-0" />
        <div>
          One-shot data utilities for legacy PRs decided before the AI system was introduced. Safe to re-run — only touches rows where the AI stamp is still missing.
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <Database size={16} className="text-cyan-700" />
          <div className="text-sm font-semibold text-slate-800">
            Backfill <code className="text-[12px] bg-slate-100 px-1.5 py-0.5 rounded mono">PoRequesti.AIResult</code> for legacy decisions
          </div>
        </div>
        <div className="p-4 space-y-3 text-[13px] text-slate-700">
          <p>
            Writes a synthesised AI stamp <code className="text-[11px] bg-slate-100 px-1 mono">ACTION|user|datetime|legacy-backfill</code> onto every line item whose parent PR is already decided (Approved / Reject / Cancel) but has a NULL <code className="mono text-[11px]">AIResult</code>. Skips Open / Authorised PRs.
          </p>
          <p className="text-[12px] text-slate-500">
            Runs asynchronously in batches of 5,000. Status polls every 3s.
          </p>

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button
              variant="outline"
              onClick={doDryRun}
              disabled={dryRunBusy || submitBusy || running}
              data-testid="backfill-dry-run-btn"
            >
              {dryRunBusy && <Loader2 size={14} className="animate-spin mr-1.5" />}
              Dry-run (count only)
            </Button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  className="bg-cyan-700 hover:bg-cyan-800 text-white"
                  disabled={!preview || preview.total_line_items === 0 || dryRunBusy || submitBusy || running}
                  data-testid="backfill-run-btn"
                >
                  {submitBusy && <Loader2 size={14} className="animate-spin mr-1.5" />}
                  Run backfill on {preview ? fmtInt(preview.total_line_items) : 0} rows
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle size={18} className="text-amber-600" /> Confirm legacy backfill</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will UPDATE <b>{preview ? fmtInt(preview.total_line_items) : 0} line items</b> in <code className="mono">PoRequesti</code> on the live MS SQL instance. The operation runs in the background — you can leave this screen and come back.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-cyan-700 hover:bg-cyan-800"
                    onClick={doRealRun}
                    data-testid="backfill-confirm-btn"
                  >
                    Yes, run backfill
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          {preview && (
            <div className="mt-3 border border-cyan-200 bg-cyan-50/50 rounded-sm overflow-hidden" data-testid="backfill-preview">
              <div className="px-3 py-2 text-[11px] uppercase tracking-wider font-semibold text-cyan-800 bg-cyan-100 border-b border-cyan-200">Dry-run scope</div>
              <table className="w-full text-[12px]">
                <thead className="bg-cyan-50 text-cyan-900">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-semibold">Status</th>
                    <th className="text-right px-3 py-1.5 font-semibold">PRs</th>
                    <th className="text-right px-3 py-1.5 font-semibold">Line Items</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview.by_status || []).map((r) => (
                    <tr key={r.status} className="border-t border-cyan-100">
                      <td className="px-3 py-1.5 font-medium">{r.status}</td>
                      <td className="px-3 py-1.5 text-right mono">{fmtInt(r.prs)}</td>
                      <td className="px-3 py-1.5 text-right mono">{fmtInt(r.line_items)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-cyan-300 bg-cyan-100/60 font-bold">
                    <td className="px-3 py-1.5">Total</td>
                    <td className="px-3 py-1.5 text-right mono">—</td>
                    <td className="px-3 py-1.5 text-right mono">{fmtInt(preview.total_line_items)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {running && (
            <div className="mt-3 border border-amber-200 bg-amber-50/60 rounded-sm p-3 text-[13px] space-y-2" data-testid="backfill-progress">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 font-semibold text-amber-900">
                  <Loader2 size={14} className="animate-spin" /> Backfill running…
                </div>
                <span className="mono text-[12px]">{fmtInt(status?.total_affected)} / {fmtInt(status?.total_target)} ({progress}%)</span>
              </div>
              <div className="w-full bg-amber-200/50 rounded-full h-2 overflow-hidden">
                <div className="bg-cyan-700 h-2 transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
              <div className="text-[11px] text-amber-800">Batch {fmtInt(status?.loops)} · {fmtInt(status?.batch_size)} rows/batch · started by {status?.started_by}</div>
            </div>
          )}

          {!running && lastRun && (
            <div className="mt-3 border border-emerald-200 bg-emerald-50/60 rounded-sm p-3 text-[13px] flex items-start gap-2" data-testid="backfill-result">
              <CheckCircle2 size={18} className="text-emerald-700 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-emerald-900">
                  Last run: backfilled {fmtInt(lastRun.total_affected)} line items in {lastRun.loops} batch{lastRun.loops === 1 ? "" : "es"} · {Math.round((lastRun.elapsed_ms || 0) / 1000)}s
                </div>
                {status?.error && (
                  <div className="text-[11px] text-rose-700 mt-1">Last error: {status.error}</div>
                )}
              </div>
            </div>
          )}

          {!running && status && !lastRun && !status?.started_at && (
            <div className="mt-3 text-[12px] text-slate-500 italic">No backfill has been run on this server yet.</div>
          )}
        </div>
      </div>

      <CacheSyncCard />
    </div>
  );
}

// ---------- Cache Sync card ----------
function CacheSyncCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  // Per-button busy flags so the user sees which sub-sync is running.
  const [busyItemMaster, setBusyItemMaster] = useState(false);
  const [busyGpurci, setBusyGpurci] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await admin.cacheStatus();
      setStatus(data);
      return data;
    } catch { return null; }
  }, []);

  useEffect(() => {
    fetchStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchStatus]);

  const startSync = async () => {
    setBusy(true);
    try {
      await admin.cacheSync();
      toast.success("Sync started — refreshing status every 5s…");
      // Snapshot baseline so we know when it has finished.
      const baseline = await fetchStatus();
      const baseTs = JSON.stringify({
        s: baseline?.suppliers?.last_synced_at,
        i: baseline?.items?.last_synced_at,
        e: baseline?.employees?.last_synced_at,
        m: baseline?.master?.last_synced_at,
      });
      const startedAt = Date.now();
      pollRef.current = setInterval(async () => {
        const s = await fetchStatus();
        const curTs = JSON.stringify({
          s: s?.suppliers?.last_synced_at,
          i: s?.items?.last_synced_at,
          e: s?.employees?.last_synced_at,
          m: s?.master?.last_synced_at,
        });
        if (curTs !== baseTs) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setBusy(false);
          toast.success("Cache refresh complete");
        } else if (Date.now() - startedAt > 90_000) {
          // Safety net — backend hangs (typically lock contention against
          // the live ERP). Stop spinning and tell the user.
          clearInterval(pollRef.current);
          pollRef.current = null;
          setBusy(false);
          toast.error(
            "Sync did not complete in 90s — the MS SQL query may be blocked " +
            "on locks held by the live ERP. Check backend logs."
          );
        }
      }, 5000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed to start");
      setBusy(false);
    }
  };

  // Sub-sync trigger shared by the two new "items breakdown" buttons. Polls
  // until the corresponding timestamp under `items_breakdown` advances.
  // Safety: bail out after 90s with an error toast so the spinner can't hang
  // forever if the backend background task crashes silently.
  const startSubSync = async (which) => {
    const setBusyFn = which === "item_master" ? setBusyItemMaster : setBusyGpurci;
    const label = which === "item_master" ? "Item master" : "GPurci rates";
    setBusyFn(true);
    try {
      if (which === "item_master") await admin.cacheSyncItemMaster();
      else await admin.cacheSyncGpurci();
      toast.success(`${label} sync started…`);
      const baseline = await fetchStatus();
      const baseTs = baseline?.items_breakdown?.[which]?.last_synced_at || "";
      const startedAt = Date.now();
      const poll = setInterval(async () => {
        const s = await fetchStatus();
        const cur = s?.items_breakdown?.[which] || {};
        const curTs = cur.last_synced_at || "";
        if (curTs && curTs !== baseTs) {
          clearInterval(poll);
          setBusyFn(false);
          if (cur.ok === false || cur.error) {
            toast.error(`${label} sync failed: ${cur.error || "unknown error"}`);
          } else {
            toast.success(`${label} sync complete · ${fmtInt(cur.count)} rows`);
          }
        } else if (Date.now() - startedAt > 90_000) {
          clearInterval(poll);
          setBusyFn(false);
          toast.error(
            `${label} sync did not complete in 90s — check backend logs. ` +
            `The DB query is likely blocked on locks held by the live ERP.`
          );
        }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${label} sync failed to start`);
      setBusyFn(false);
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return "never";
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  };
  const sections = [
    { key: "suppliers", label: "Suppliers (party)" },
    { key: "items", label: "Items (master + GPurci)" },
    { key: "employees", label: "Active Employees" },
    { key: "master", label: "Master (Plant / Dept / Group)" },
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="cache-sync-card">
      <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center gap-2">
        <RefreshCw size={16} className="text-cyan-700" />
        <div className="text-sm font-semibold text-slate-800">Reference Data Cache</div>
        <span className="text-[11px] text-slate-500 ml-auto">
          auto-refresh every {Math.round((status?.interval_seconds || 21600) / 3600)}h
        </span>
      </div>
      <div className="p-4 space-y-3 text-[13px] text-slate-700">
        <p className="text-[12px] text-slate-600">
          Suppliers, items, employees and plant/dept/group lists are mirrored from MS SQL into a local cache so PR-creation autocomplete is sub-100ms. New entries in legacy ERP appear here at most {Math.round((status?.interval_seconds || 21600) / 3600)} hours later — or click <strong>Sync now</strong> if you can&apos;t wait.
        </p>

        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-[12.5px]">
            <thead className="bg-slate-100 text-slate-600 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Collection</th>
                <th className="px-3 py-2 text-right font-semibold">Rows</th>
                <th className="px-3 py-2 text-right font-semibold">Last sync</th>
                <th className="px-3 py-2 text-right font-semibold">Duration</th>
              </tr>
            </thead>
            <tbody>
              {sections.map((s) => {
                const v = status?.[s.key] || {};
                return (
                  <tr key={s.key} className="border-t border-slate-200" data-testid={`cache-row-${s.key}`}>
                    <td className="px-3 py-2 text-slate-800">{s.label}</td>
                    <td className="px-3 py-2 text-right mono">{fmtInt(v.rows)}</td>
                    <td className="px-3 py-2 text-right text-slate-600">{fmtTime(v.last_synced_at)}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{v.last_elapsed_ms != null ? `${Math.round(v.last_elapsed_ms / 100) / 10}s` : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Items sub-syncs — surfaced so an admin can refresh just the master
            (after creating new items in the ERP) or just the rate signals
            (after a big PO load) without paying for the full sync. */}
        <div className="border border-cyan-200 rounded-sm overflow-hidden bg-cyan-50/30" data-testid="items-breakdown-card">
          <div className="bg-cyan-100/60 px-3 py-2 text-[11px] uppercase tracking-wider font-semibold text-cyan-900 border-b border-cyan-200">
            Items cache · breakdown
          </div>
          <table className="w-full text-[12.5px]">
            <thead className="bg-cyan-50 text-cyan-900 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Source</th>
                <th className="px-3 py-2 text-right font-semibold">Rows</th>
                <th className="px-3 py-2 text-right font-semibold">Last sync</th>
                <th className="px-3 py-2 text-right font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-cyan-100" data-testid="cache-row-item-master">
                <td className="px-3 py-2 text-slate-800">
                  <div className="font-medium">Item master (<code className="text-[11px] bg-white px-1 mono">Item</code> table)</div>
                  <div className="text-[11px] text-slate-500">All items that exist in the legacy ERP — even those without any PR history</div>
                </td>
                <td className="px-3 py-2 text-right mono">{fmtInt(status?.items_breakdown?.item_master?.count)}</td>
                <td className="px-3 py-2 text-right text-slate-600">{fmtTime(status?.items_breakdown?.item_master?.last_synced_at)}</td>
                <td className="px-3 py-2 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => startSubSync("item_master")}
                    disabled={busy || busyItemMaster || busyGpurci}
                    data-testid="cache-sync-item-master-btn"
                  >
                    {busyItemMaster ? <Loader2 size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />}
                    Sync
                  </Button>
                </td>
              </tr>
              <tr className="border-t border-cyan-100" data-testid="cache-row-gpurci">
                <td className="px-3 py-2 text-slate-800">
                  <div className="font-medium">GPurci rates (<code className="text-[11px] bg-white px-1 mono">GPurci</code> table)</div>
                  <div className="text-[11px] text-slate-500">Last-purchase rates per icode — drives the &quot;last rate&quot; signal in PR creation</div>
                </td>
                <td className="px-3 py-2 text-right mono">{fmtInt(status?.items_breakdown?.gpurci?.count)}</td>
                <td className="px-3 py-2 text-right text-slate-600">{fmtTime(status?.items_breakdown?.gpurci?.last_synced_at)}</td>
                <td className="px-3 py-2 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => startSubSync("gpurci")}
                    disabled={busy || busyItemMaster || busyGpurci}
                    data-testid="cache-sync-gpurci-btn"
                  >
                    {busyGpurci ? <Loader2 size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />}
                    Sync
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between pt-1">
          <span className="text-[11px] text-slate-500">
            Sync runs in the background — safe to leave this page.
          </span>
          <Button
            onClick={startSync}
            disabled={busy || busyItemMaster || busyGpurci}
            data-testid="cache-sync-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <RefreshCw size={14} className="mr-1.5" />}
            Sync now (all)
          </Button>
        </div>
      </div>
    </div>
  );
}
